import React, { useState, useEffect, useCallback } from 'react';
import { dailyLogApi, projectApi, teamApi, wbsApi } from '../services/api';
import { Card, Button, Input, Select, Modal, Badge, Spinner } from '../components/UI';
import { Plus, PencilSimple, Trash, CalendarBlank, Lock, Camera, Images, X, DownloadSimple, Eye } from '@phosphor-icons/react';

const MAX_FILE_SIZE_MB = 10;
const MAX_ATTACHMENTS = 5;

function formatFileSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / 1048576).toFixed(1) + ' MB';
}

function AttachmentGallery({ logId, attachments = [], onRefresh, readOnly }) {
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      alert(`File too large. Maximum size is ${MAX_FILE_SIZE_MB} MB.`);
      e.target.value = '';
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await dailyLogApi.uploadAttachment(logId, formData);
      onRefresh();
    } catch (err) {
      alert(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm('Delete this photo?')) return;
    try {
      await dailyLogApi.deleteAttachment(logId, fileId);
      onRefresh();
    } catch (err) {
      alert(err.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="mt-2">
      <div className="flex items-center gap-2 flex-wrap">
        {attachments.map((att) => (
          <div key={att.id} className="relative group" data-testid={`attachment-${att.id}`}>
            <img
              src={dailyLogApi.getFileUrl(att.filename)}
              alt={att.original_name}
              className="w-14 h-14 rounded-md object-cover border border-[#E0E0E0] cursor-pointer hover:ring-2 hover:ring-[#002FA7] transition-all"
              onClick={() => setLightbox(att)}
            />
            {!readOnly && (
              <button
                onClick={() => handleDelete(att.id)}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-[#FF3333] text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`delete-attachment-${att.id}`}
              >
                <X className="w-3 h-3" weight="bold" />
              </button>
            )}
          </div>
        ))}
        {!readOnly && attachments.length < MAX_ATTACHMENTS && (
          <label
            className={`w-14 h-14 rounded-md border-2 border-dashed border-[#CCC] flex flex-col items-center justify-center cursor-pointer hover:border-[#002FA7] hover:bg-[#F5F7FF] transition-colors ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
            data-testid={`upload-photo-${logId}`}
          >
            {uploading ? (
              <Spinner size="sm" />
            ) : (
              <>
                <Camera className="w-5 h-5 text-[#999]" />
                <span className="text-[8px] text-[#999] mt-0.5">Photo</span>
              </>
            )}
            <input type="file" accept="image/*" className="hidden" onChange={handleUpload} disabled={uploading} />
          </label>
        )}
      </div>
      {attachments.length > 0 && (
        <p className="text-[9px] text-[#999] mt-1">{attachments.length}/{MAX_ATTACHMENTS} photos (max {MAX_FILE_SIZE_MB} MB each)</p>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="photo-lightbox">
          <div className="relative max-w-3xl max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
            <img
              src={dailyLogApi.getFileUrl(lightbox.filename)}
              alt={lightbox.original_name}
              className="max-w-full max-h-[80vh] rounded-lg object-contain"
            />
            <div className="absolute top-2 right-2 flex gap-2">
              <a
                href={dailyLogApi.getFileUrl(lightbox.filename)}
                download={lightbox.original_name}
                target="_blank"
                rel="noreferrer"
                className="p-2 bg-white/90 rounded-full hover:bg-white transition-colors"
                data-testid="download-photo-btn"
              >
                <DownloadSimple className="w-4 h-4 text-[#333]" />
              </a>
              <button onClick={() => setLightbox(null)} className="p-2 bg-white/90 rounded-full hover:bg-white transition-colors" data-testid="close-lightbox-btn">
                <X className="w-4 h-4 text-[#333]" />
              </button>
            </div>
            <div className="text-center mt-2">
              <p className="text-white text-sm">{lightbox.original_name}</p>
              <p className="text-white/60 text-xs">{formatFileSize(lightbox.size)} — {lightbox.uploaded_by_name}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function DailyLogsPage() {
  const [logs, setLogs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [teams, setTeams] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [filterProject, setFilterProject] = useState('');
  const [filterTeam, setFilterTeam] = useState('');
  const [filterDate, setFilterDate] = useState(new Date().toISOString().split('T')[0]);

  // Form
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    project_id: '', team_id: '', activity_id: '', log_date: '',
    work_description: '', progress_pct: 0, remarks: ''
  });
  const [formTeams, setFormTeams] = useState([]);
  const [formActivities, setFormActivities] = useState([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([projectApi.list(), wbsApi.listActivities()])
      .then(([pRes, aRes]) => { setProjects(pRes.data); setActivities(aRes.data); })
      .finally(() => setLoading(false));
  }, []);

  const loadLogs = useCallback(async () => {
    const params = {};
    if (filterProject) params.project_id = filterProject;
    if (filterTeam) params.team_id = filterTeam;
    if (filterDate) params.log_date = filterDate;
    const res = await dailyLogApi.list(params);
    // Load attachments for each log
    const logsWithAttachments = await Promise.all(
      res.data.map(async (log) => {
        try {
          const attRes = await dailyLogApi.listAttachments(log.id);
          return { ...log, attachments: attRes.data };
        } catch {
          return { ...log, attachments: [] };
        }
      })
    );
    setLogs(logsWithAttachments);
  }, [filterProject, filterTeam, filterDate]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  // Load teams when project filter changes
  useEffect(() => {
    if (filterProject) {
      teamApi.list(filterProject).then(r => setTeams(r.data));
    } else {
      teamApi.list().then(r => setTeams(r.data));
    }
  }, [filterProject]);

  // Cascade: form project -> teams
  useEffect(() => {
    if (form.project_id) {
      teamApi.list(form.project_id).then(r => setFormTeams(r.data));
    } else {
      setFormTeams([]);
    }
  }, [form.project_id]);

  // Cascade: load activities for form
  useEffect(() => {
    wbsApi.listActivities().then(r => setFormActivities(r.data));
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({
      project_id: filterProject || '', team_id: filterTeam || '',
      activity_id: '', log_date: filterDate || new Date().toISOString().split('T')[0],
      work_description: '', progress_pct: 0, remarks: ''
    });
    setError('');
    setShowModal(true);
  };

  const openEdit = (log) => {
    if (log.is_locked) return;
    setEditing(log);
    setForm({
      project_id: log.project_id, team_id: log.team_id, activity_id: log.activity_id,
      log_date: log.log_date, work_description: log.work_description,
      progress_pct: log.progress_pct, remarks: log.remarks || ''
    });
    setError('');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSaving(true);
    try {
      const data = { ...form, progress_pct: parseFloat(form.progress_pct) || 0 };
      if (editing) {
        await dailyLogApi.update(editing.id, data);
      } else {
        await dailyLogApi.create(data);
      }
      setShowModal(false);
      loadLogs();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this log entry?')) return;
    try { await dailyLogApi.delete(id); loadLogs(); }
    catch (err) { alert(err.response?.data?.detail || 'Failed to delete'); }
  };

  if (loading) return <div className="flex justify-center py-8"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-4" data-testid="daily-logs-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0A0A0A]">Daily Site Logs</h1>
          <p className="text-sm text-[#666666]">Record daily work progress per team and activity</p>
        </div>
        <Button onClick={openCreate} data-testid="add-daily-log-btn">
          <Plus className="w-4 h-4 mr-2" /> New Entry
        </Button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Select value={filterProject} onChange={(e) => { setFilterProject(e.target.value); setFilterTeam(''); }} data-testid="filter-project">
          <option value="">All Projects</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Select>
        <Select value={filterTeam} onChange={(e) => setFilterTeam(e.target.value)} data-testid="filter-team">
          <option value="">All Teams</option>
          {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </Select>
        <Input type="date" value={filterDate} onChange={(e) => setFilterDate(e.target.value)} data-testid="filter-date" />
      </div>

      {/* Entries */}
      <div className="space-y-2">
        {logs.length === 0 ? (
          <Card className="p-6 text-center text-[#666666]">No logs for this date/filter</Card>
        ) : logs.map(log => (
          <Card key={log.id} className={`p-3 sm:p-4 ${log.is_locked ? 'border-l-4 border-l-[#FFAB00]' : ''}`} data-testid={`log-entry-${log.id}`}>
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-[#0A0A0A]">{log.activity_name}</span>
                  <span className="text-[10px] bg-[#E5E5E5] px-1.5 py-0.5 rounded font-mono">{log.activity_trade}</span>
                  {log.is_locked && <Lock className="w-3.5 h-3.5 text-[#FFAB00]" weight="fill" />}
                  {log.attachments?.length > 0 && (
                    <span className="text-[10px] bg-[#E8F5E9] text-[#2E7D32] px-1.5 py-0.5 rounded flex items-center gap-0.5">
                      <Images className="w-3 h-3" /> {log.attachments.length}
                    </span>
                  )}
                </div>
                <p className="text-sm text-[#333] mt-1">{log.work_description}</p>
                <div className="flex flex-wrap items-center gap-3 mt-1.5 text-xs text-[#666666]">
                  <span>{log.team_name}</span>
                  <span>{log.project_name}</span>
                  <span className="flex items-center gap-0.5"><CalendarBlank className="w-3 h-3" />{log.log_date}</span>
                </div>
                {log.remarks && <p className="text-xs text-[#999] mt-1 italic">{log.remarks}</p>}

                {/* Photo Gallery */}
                <AttachmentGallery
                  logId={log.id}
                  attachments={log.attachments || []}
                  onRefresh={loadLogs}
                  readOnly={log.is_locked}
                />
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <div className="flex items-center gap-1.5">
                  <div className="w-16 h-1.5 bg-[#E0E0E0] rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${log.progress_pct}%`, backgroundColor: log.progress_pct >= 100 ? '#00C853' : '#002FA7' }} />
                  </div>
                  <span className="text-xs font-mono text-[#666]" data-testid={`log-progress-${log.id}`}>{log.progress_pct}%</span>
                </div>
                {!log.is_locked && (
                  <>
                    <button onClick={() => openEdit(log)} className="p-1 text-[#666] hover:text-[#002FA7]" data-testid={`edit-log-${log.id}`}><PencilSimple className="w-4 h-4" /></button>
                    <button onClick={() => handleDelete(log.id)} className="p-1 text-[#666] hover:text-[#FF3333]" data-testid={`delete-log-${log.id}`}><Trash className="w-4 h-4" /></button>
                  </>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Form Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editing ? 'Edit Log' : 'New Daily Log'}>
        <form onSubmit={handleSubmit} className="space-y-3" data-testid="daily-log-form">
          {error && <div className="p-2 bg-[#FF3333]/10 text-[#FF3333] rounded text-sm" data-testid="form-error">{error}</div>}
          <Input label="Date" type="date" value={form.log_date} onChange={(e) => setForm({ ...form, log_date: e.target.value })} required data-testid="log-date-input" />
          <Select label="Project" value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value, team_id: '' })} required data-testid="log-project-select">
            <option value="">-- Select Project --</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Select>
          <Select label="Team" value={form.team_id} onChange={(e) => setForm({ ...form, team_id: e.target.value })} required data-testid="log-team-select">
            <option value="">-- Select Team --</option>
            {formTeams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </Select>
          <Select label="Activity" value={form.activity_id} onChange={(e) => setForm({ ...form, activity_id: e.target.value })} required data-testid="log-activity-select">
            <option value="">-- Select Activity --</option>
            {formActivities.map(a => <option key={a.id} value={a.id}>{a.trade} / {a.subcategory} — {a.activity_name}</option>)}
          </Select>
          <Input label="Work Description" value={form.work_description} onChange={(e) => setForm({ ...form, work_description: e.target.value })} required placeholder="Describe work done" data-testid="log-description-input" />
          <Input label="Progress %" type="number" min="0" max="100" value={form.progress_pct} onChange={(e) => setForm({ ...form, progress_pct: e.target.value })} required data-testid="log-progress-input" />
          <Input label="Remarks" value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} placeholder="Optional notes" data-testid="log-remarks-input" />
          <p className="text-[10px] text-[#999]">You can attach site photos after saving the log entry (max {MAX_ATTACHMENTS} photos, {MAX_FILE_SIZE_MB} MB each)</p>
          <div className="flex justify-end space-x-3 pt-3">
            <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="save-log-btn">{saving ? 'Saving...' : editing ? 'Update' : 'Save'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
